<template>
	<div class="business-picker-container">
		<!-- Business Card Section -->
		<div class="business-card">
			<div class="business-logo" v-if="currBusiness">
				<img v-if="currBusiness.logo" :src="currBusiness.logo" alt="Business logo">
				<div v-else class="business-logo-placeholder">
					<img src="https://img.qromo.io/img/logo.svg" alt="Default logo">
				</div>
			</div>
			
			<div class="business-content">
				<div class="business-header">
					<h2 class="business-name">{{ currBusiness ? currBusiness.name : '' }}</h2>
					<div class="business-actions">
						<AppButton 
							v-if="hasMoreThanOneBusiness"
							:text="getString('switch_business')"
							@click="handleSwitchBusiness"
							class="btn-switch"
						/>
						<AppButton 
							:text="getString('new_business')"
							icon-left="icon-plus"
							@click="handleNewBusiness"
							:outline="true"
							class="btn-new"
						/>
						<AppButton
							:text="getString('subscription')"
							@click="handleSubscription"
							:outline="true"
							class="btn-new"
						/>
					</div>
				</div>
				
				<div class="business-details">
					<div class="detail-item">
						<span class="detail-label">{{ getString('business_email') }}</span>
						<span class="detail-value">{{ currBusiness ? currBusiness.company_email : '-' }}</span>
					</div>
					<div class="detail-item">
						<span class="detail-label">{{ getString('business_address') }}</span>
						<span class="detail-value">{{ businessAddress }}</span>
					</div>
					<div class="detail-item">
						<span class="detail-label">{{ getString('subscription') }}</span>
						<span class="detail-value" :class="subscriptionStatusClass">{{ subscriptionStatus }}</span>
					</div>
					<div class="detail-item">
						<span class="detail-label">P.IVA</span>
						<span class="detail-value">{{ currBusiness && currBusiness.vat ? currBusiness.vat : '-' }}</span>
					</div>
				</div>
			</div>
		</div>
		
		<!-- User Section -->
		<div class="user-section" v-if="user">
			<div class="user-picture" v-if="user.picture"><img :src="user.picture"></div>
			<div class="user-avatar" v-else>{{ userInitials }}</div>
			<div class="user-info">
				<span class="user-label">{{ getString('authenticated_user') }}</span>
				<span class="user-name">{{ user.name }}</span>
			</div>
			<div class="user-actions">
				<AppButton
					:text="getString('console_user')"
					@click="handleConsoleUser"
					outline
					class="btn-console-user"
				/>
				<AppButton
					:text="getString('logout')"
					icon-left="icon-logout-primary"
					:loading="loggingOut"
					@click="handleLogout"
					outline
					class="btn-logout"
				/>
			</div>
		</div>
		
		<!-- Business Switcher Bottom Sheet -->
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="businessSwitcherActive" @toggling-popup="toggleBusinessSwitcher">
			<BusinessSwitcher v-if="businessSwitcherActive" @done="businessSwitcherActive = false;$emit('done')" />
		</AppBottomSheet>
	</div>
</template>

<script>
import {defineComponent} from 'vue'
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import BusinessSwitcher from "@/console-new/components/shared/BusinessSwitcher.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default defineComponent({
	name: "BusinessPicker",
	components: {AppButton, AppBottomSheet, BusinessSwitcher},
	mixins: [WindowWidth],
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {
			userStore,
			getString: languageStore.getString
		}
	},
	emits: ["done"],
	data() {
		return {
			businessSwitcherActive: false,
			loggingOut: false
		}
	},
	computed: {
		currBusiness() {
			return this.userStore.currBusiness;
		},
		user() {
			return this.userStore.user;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		businessAddress() {
			if(this.currBusiness && this.currBusiness.address && this.currBusiness.address.address) {
				return this.currBusiness.address.address;
			}
			return '-';
		},
		userInitials() {
			if (!this.user) return '';
			const name = this.user.name || '';
			// if name is explodable by space, take first and second letter
			const parts = name.split(' ');
			if (parts.length > 1) {
				return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
			} else {
				return name.charAt(0).toUpperCase();
			}
		},
		hasMoreThanOneBusiness() {
			return this.userStore.businesses && Object.keys(this.userStore.businesses).length > 1;
		},
		subscriptionStatus() {
			if (!this.currBusiness) return '-';
			
			// Check if there's no active plan
			if (!this.currBusiness.active_plan || !this.currBusiness.plan_id) {
				return this.getString('not_subscribed');
			}
			
			// Format the plan name
			let planName = '-';
			if (this.currBusiness.active_plan) {
				switch(this.currBusiness.active_plan) {
					case 'basic':
						planName = 'Basic';
						break;
					case 'pro':
						planName = 'Pro';
						break;
					case 'allinone':
						planName = 'All-in-one';
						break;
					case 'enterprise':
						planName = 'Enterprise';
						break;
					case 'old':
						planName = 'Base';
						break;
					default:
						planName = this.currBusiness.active_plan;
				}
			}
			
			// Add status if applicable
			if (this.currBusiness.plan_status === 'past_due') {
				return `${planName} (${this.getString('past_due')})`;
			} else if (this.currBusiness.plan_status === 'unpaid') {
				return `${planName} (${this.getString('unpaid')})`;
			} else if (this.currBusiness.plan_status === 'trialing') {
				return `${planName} (${this.getString('trial')})`;
			}
			
			return planName;
		},
		subscriptionStatusClass() {
			if (!this.currBusiness) return '';
			
			// Check if not subscribed
			if (!this.currBusiness.active_plan || !this.currBusiness.plan_id) {
				return 'status-not-subscribed';
			}
			
			if (this.currBusiness.plan_status === 'past_due' || this.currBusiness.plan_status === 'unpaid') {
				return 'status-warning';
			}
			
			return '';
		}
	},
	methods: {
		handleSwitchBusiness() {
			this.businessSwitcherActive = true;
		},
		toggleBusinessSwitcher(active) {
			if(!active) {
				this.businessSwitcherActive = false;
			}
		},
		handleNewBusiness() {
			this.$router.push("/add-business");
			this.$emit("done");
		},
		handleSubscription() {
			this.$router.push("/plugins/marketplace");
			this.$emit("done");
		},
		handleLogout() {
			this.loggingOut = true;
			this.userStore.logout(() => {
				this.loggingOut = false;
			});
		},
		handleConsoleUser() {
			window.location.href = URL_HTML + '/user';
		}
	}
})
</script>

<style lang="scss" scoped>
.business-picker-container {
	background-color: var(--color-bg-primary-1);
	border-radius: 26px 26px 0 0;
	display: flex;
	flex-direction: column;
	gap: 20px;

	.business-card {
		background-color: var(--color-alpha-1);
		border-radius: 28px;
		padding: 20px;
		display: flex;
		gap: 20px;
		box-shadow: inset -2px -2px 4px 0 var(--color-light-shadow-button), 
					inset 2px 2px 4px 0 var(--color-shadow-button);

		
		.business-logo {
			width: 80px;
			height: 80px;
			flex-shrink: 0;

			@media (max-width: 350px) {
				display: none;
			}

			img {
				width: 100%;
				height: 100%;
				border-radius: 13px;
				object-fit: cover;
			}
			
			.business-logo-placeholder {
				width: 100%;
				height: 100%;
				background-color: var(--color-bg-primary-1);
				border-radius: 13px;
				display: flex;
				align-items: center;
				justify-content: center;
				
				img {
					width: 40px;
					height: 40px;
				}
			}
		}
		
		.business-content {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 15px;

			.business-header {
				border-bottom: 1px solid var(--color-border-1);
				padding-bottom: 10px;

				
				.business-name {
					font-size: 24px;
					font-weight: bold;
					color: var(--color-txt);
					margin: 0 0 10px 0;
					font-family: 'Montserrat', sans-serif;
				}
				
				.business-actions {
					display: flex;
					gap: 10px;
					flex-wrap: wrap;
					justify-content: flex-end;
					
					:deep(.base-button) {
						min-width: auto;
						padding: 8px 16px;
						height: 38px;
						font-size: 16px;
						font-family: 'Montserrat', sans-serif;
						font-weight: 500;
						
						&.btn-switch {
							background-color: var(--color-primary);
							color: var(--color-txt-white);
							border: none;
							
							&:hover {
								background-color: var(--color-primary);
								filter: brightness(0.9);
							}
						}
						
						&.btn-new, &.btn-logout {
							border: 1.5px solid var(--color-primary);
							color: var(--color-primary);
							background-color: transparent;
							
							&:hover {
								background-color: var(--color-fill-button);
							}
						}
					}
				}

				@media (max-width: 768px) {
					border-bottom: none;

					.business-actions {
						flex-direction: column;
						align-items: flex-end;
					}
				}
			}
			
			.business-details {
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 15px 20px;
				
				.detail-item {
					display: flex;
					flex-direction: column;
					gap: 3px;
					
					.detail-label {
						font-size: 12px;
						color: var(--color-txt-2);
						font-family: 'Montserrat', sans-serif;
						font-weight: 500;
					}
					
					.detail-value {
						font-size: 14px;
						color: var(--color-txt);
						font-family: 'Montserrat', sans-serif;
						font-weight: 500;


						&.status-not-subscribed {
							font-style: italic;
							font-weight: 700;
						}

						&.status-warning {
							color: var(--color-danger);
							font-weight: 600;
						}
					}
				}
			}
		}
	}
	
	.user-section {
		background-color: rgba(var(--rgb-primary-user-console), 0.1);
		border-radius: 13px;
		padding: 15px 20px;
		display: flex;
		align-items: center;
		gap: 20px;

		.user-picture {
			width: 50px;
			height: 50px;
			min-width: 50px;
			border-radius: 13px;
			overflow: hidden;
			flex-shrink: 0;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
		
		.user-avatar {
			width: 50px;
			height: 50px;
			min-width: 50px;
			background-color: var(--color-txt-1);
			border-radius: 13px;
			display: flex;
			align-items: center;
			justify-content: center;
			color: var(--color-txt-white);
			font-size: 21px;
			font-weight: 500;
			font-family: 'Montserrat', sans-serif;
		}
		
		.user-info {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 2px;
			
			.user-label {
				font-size: 12px;
				color: var(--color-txt-2);
				font-family: 'Montserrat', sans-serif;
				font-weight: 500;
			}
			
			.user-name {
				font-size: 18px;
				font-weight: bold;
				color: var(--color-txt);
				font-family: 'Montserrat', sans-serif;
			}
		}
		.user-actions {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			gap: 10px;
		}
		
		:deep(.btn-console-user), :deep(.btn-logout) {
			min-width: auto;
			padding: 8px 16px;
			height: 38px;
			font-size: 16px;
			border-color: var(--color-primary);
			color: var(--color-primary);
			background-color: transparent;
			
			&:hover {
				background-color: var(--color-fill-button);
			}
		}


		@media (max-width: 768px) {
			align-items: flex-start;
			.user-actions {
				align-items: flex-end;
				flex-direction: column;
			}
		}
		@media (max-width: 400px) {
			flex-wrap: wrap;
			.user-actions {
				width: 100%;
			}
		}

	}
	
	@media (max-width: 768px) {
		.business-card {
			.business-content {
				.business-details {
					display: none;
				}
			}
		}
	}
}
</style>